/**
 * ============================================================================
 * CSV HELPER - csvHelper.js
 * ============================================================================
 * CSV generation and download utilities for inspection data export
 * Extracted from App.js Phase 3D
 */

import { formatDate } from './dateHelper';
import { extractAdditionalNotes } from './inspectionHelper';

/**
 * Generate CSV content from inspections array
 * @param {Array} inspections - Array of inspection objects
 * @param {Function} getSubmittedByName - Function to get submitter name
 * @param {Function} getUserTotalPoints - Function to get user points
 * @param {Object} options - Optional settings { baseUrl }
 * @returns {string} CSV content
 */
export const generateCSV = (inspections, getSubmittedByName, getUserTotalPoints, options = {}) => {
  // 🆕 Determine base URL for image links (auto-detect from current window location)
  const baseUrl = options.baseUrl || (typeof window !== 'undefined' ? window.location.origin : 'https://dev-tagquest.pyebarkerfs.com');
  
  // 🆕 Sort by created_at for chronological Lead # numbering
  const sortedInspections = [...inspections].sort((a, b) => {
    const dateA = new Date(a.created_at || a.inspection_date || 0);
    const dateB = new Date(b.created_at || b.inspection_date || 0);
    return dateA - dateB;
  });
  
  // 🆕 Lead # and Image Link as first columns for easy access
  const headers = ['Lead #', 'Image Link', 'Inspection ID', 'Date', 'Submitted At', 'Updated At', 'Location', 'Business Address', 'Type', /* 'Test Service Co.', */ /* 'Condition', */ 'Last Inspection', 'Next Due', 'Working Service Co.', 'Working Phone', 'Working Notes', 'Equipment Numbers', 'Service Type', 'GPS Coordinates', 'GPS Source', 'Region', 'Assigned Branch', 'Branch Distance (km)', 'Status', 'Approval Status', 'Submitted By', 'Submitter Email', 'Regional Vice President', 'Regional Director', 'User Total Points', 'AI Confidence Score', 'Human Review', 'Notes', /* 'Main Page Voice Audio', 'Main Page Voice Notes', */ 'Main Page Attachments', 'Additional Images', 'Additional Notes', 'Image Metadata'];
  const csvRows = [headers.join(',')];

  sortedInspections.forEach((inspection, index) => {
    // 🆕 Lead number (1-based)
    const leadNum = index + 1;
    
    // 🆕 Full clickable image URL
    const inspectionId = inspection.id || inspection._id || '';
    const imageLink = inspectionId ? `${baseUrl}/api/inspections/${inspectionId}/image` : '';
    let parsed = {};
    try {
      if (typeof inspection.gemini_response === 'object') {
        parsed = inspection.gemini_response;
      } else if (typeof inspection.gemini_response === 'string') {
        const jsonMatch = inspection.gemini_response.match(/```json\s*(\{[\s\S]*?\})\s*```|(\{[\s\S]*\})/);
        if (jsonMatch) {
          parsed = JSON.parse(jsonMatch[1] || jsonMatch[2]);
        }
      }
    } catch (e) {
      parsed = {};
    }

    // Format equipment numbers for CSV
    const equipmentNumbers = [];
    if (parsed.equipment_numbers) {
      if (parsed.equipment_numbers.ae_number && parsed.equipment_numbers.ae_number !== 'unknown') {
        equipmentNumbers.push(`AE:${parsed.equipment_numbers.ae_number}`);
      }
      if (parsed.equipment_numbers.he_number && parsed.equipment_numbers.he_number !== 'unknown') {
        equipmentNumbers.push(`HE:${parsed.equipment_numbers.he_number}`);
      }
      if (parsed.equipment_numbers.ee_number && parsed.equipment_numbers.ee_number !== 'unknown') {
        equipmentNumbers.push(`EE:${parsed.equipment_numbers.ee_number}`);
      }
      if (parsed.equipment_numbers.fe_number && parsed.equipment_numbers.fe_number !== 'unknown') {
        equipmentNumbers.push(`FE:${parsed.equipment_numbers.fe_number}`);
      }
    }

    // Format GPS data - extract coordinates AND source
    let gpsCoordinates = 'N/A';
    let gpsSource = 'N/A';
    if (inspection.gps_data) {
      try {
        const gps = typeof inspection.gps_data === 'string' ? JSON.parse(inspection.gps_data) : inspection.gps_data;
        if (gps.latitude && gps.longitude) {
          gpsCoordinates = `${gps.latitude.toFixed(6)}, ${gps.longitude.toFixed(6)}`;
          gpsSource = gps.source || 'device';
        }
      } catch (e) {
        // If GPS data exists but can't be parsed, just note that GPS was captured
        gpsCoordinates = 'GPS Captured';
      }
    }
    
    // Extract Region from state
    const STATE_TO_REGION = {
      'TX': 'Southwest', 'OK': 'Southwest', 'AR': 'Southwest', 'LA': 'Southwest',
      'GA': 'Southeast', 'FL': 'Southeast', 'AL': 'Southeast', 'MS': 'Southeast', 
      'TN': 'Southeast', 'SC': 'Southeast', 'NC': 'Southeast',
      'PA': 'Mid-Atlantic', 'NY': 'Mid-Atlantic', 'NJ': 'Mid-Atlantic', 
      'MD': 'Mid-Atlantic', 'VA': 'Mid-Atlantic', 'WV': 'Mid-Atlantic', 'DE': 'Mid-Atlantic',
      'OH': 'Midwest', 'IN': 'Midwest', 'IL': 'Midwest', 'MI': 'Midwest', 
      'WI': 'Midwest', 'MN': 'Midwest', 'IA': 'Midwest', 'MO': 'Midwest',
      'MA': 'Northeast', 'CT': 'Northeast', 'RI': 'Northeast', 
      'VT': 'Northeast', 'NH': 'Northeast', 'ME': 'Northeast',
      'CA': 'West', 'NV': 'West', 'AZ': 'West', 'UT': 'West', 
      'CO': 'West', 'NM': 'West', 'WA': 'West', 'OR': 'West',
      'KY': 'South', 'PR': 'South'
    };
    const state = (parsed.site_state || inspection.site_state || '').toUpperCase().trim();
    const region = STATE_TO_REGION[state] || 'Unknown';
    
    // Extract AI confidence score
    const confidenceScore = parsed.confidence_score 
      ? `${Math.round(parsed.confidence_score * 100)}%` 
      : (parsed.ai_confidence || 'N/A');

    // Format image metadata with priority order: GPS → DateTime → Other
    let metadataString = 'N/A';
    if (inspection.image_metadata) {
      try {
        const metadata = typeof inspection.image_metadata === 'string'
          ? JSON.parse(inspection.image_metadata)
          : inspection.image_metadata;

        const metadataParts = [];

        // Priority 1: GPS coordinates (from EXIF)
        if (metadata.gps_latitude && metadata.gps_longitude) {
          metadataParts.push(`GPS: ${metadata.gps_latitude}, ${metadata.gps_longitude} (${metadata.gps_source || 'EXIF'})`);
        }

        // Priority 2: DateTime taken
        if (metadata.exif_datetimeoriginal) {
          const dt = new Date(metadata.exif_datetimeoriginal);
          metadataParts.push(`Taken: ${dt.toLocaleString('en-US', { timeZone: 'America/Chicago' })}`);
        } else if (metadata.exif_datetime) {
          const dt = new Date(metadata.exif_datetime);
          metadataParts.push(`Modified: ${dt.toLocaleString('en-US', { timeZone: 'America/Chicago' })}`);
        }

        // Priority 3: Image dimensions and size
        if (metadata.width && metadata.height) {
          metadataParts.push(`Size: ${metadata.width}x${metadata.height}`);
        }
        if (metadata.file_size_kb) {
          metadataParts.push(`${metadata.file_size_kb}KB`);
        }

        // Priority 4: Camera/device info
        if (metadata.exif_data) {
          if (metadata.exif_data.make && metadata.exif_data.model) {
            metadataParts.push(`Camera: ${metadata.exif_data.make} ${metadata.exif_data.model}`);
          } else if (metadata.exif_data.make) {
            metadataParts.push(`Camera: ${metadata.exif_data.make}`);
          } else if (metadata.exif_data.model) {
            metadataParts.push(`Device: ${metadata.exif_data.model}`);
          }
        }

        // Priority 5: Format info
        if (metadata.format) {
          metadataParts.push(`Format: ${metadata.format}`);
        }

        metadataString = metadataParts.join(' | ') || 'No metadata available';

      } catch (e) {
        metadataString = 'Metadata parsing error';
      }
    }

    const row = [
      leadNum,  // 🆕 Lead # (1, 2, 3...)
      `"${imageLink}"`,  // 🆕 Full clickable image URL
      `"${inspectionId}"`,  // 🆕 Inspection ID
      new Date(inspection.inspection_date).toLocaleDateString(),
      `"${inspection.created_at ? formatDate(inspection.created_at, true) : 'N/A'}"`,
      `"${inspection.updated_at ? formatDate(inspection.updated_at, true) : 'N/A'}"`,
      `"${inspection.location}"`,
      `"${parsed.business_address || inspection.business_address || 'N/A'}"`,
      `"${parsed.extinguisher_type || 'N/A'}"`,
      // `"${parsed.test_extinguisher_type || 'N/A'}"`, // HIDDEN - Test Service Co. column
      // `"${parsed.condition || 'N/A'}"`, // BLOCKED OUT - Condition column
      `"${parsed.last_inspection_date ? formatDate(parsed.last_inspection_date) : 'N/A'}"`,
      `"${parsed.next_due_date ? formatDate(parsed.next_due_date) : 'N/A'}"`,
      `"${parsed.service_company?.name || parsed.working_service_company_name || inspection.current_provider || 'N/A'}"`,
      `"${inspection.service_company_phone || parsed.service_company?.phone || parsed.working_service_company_phone || 'N/A'}"`,
      `"${parsed.additional_notes || parsed.working_additional_notes || 'N/A'}"`,
      `"${equipmentNumbers.length > 0 ? equipmentNumbers.join(' | ') : 'N/A'}"`,
      `"${parsed.service_details?.service_type && parsed.service_details.service_type !== 'unknown' ? parsed.service_details.service_type : 'N/A'}"`,
      `"${gpsCoordinates}"`,
      `"${gpsSource}"`,
      `"${region}"`,
      `"${inspection.assigned_branch ? inspection.assigned_branch.name : 'Not assigned'}"`,
      `"${inspection.branch_distance_km || 'N/A'}"`,
      `"${inspection.status}"`,
      `"${inspection.approval_status || inspection.status}"`,
      `"${getSubmittedByName(inspection)}"`,
      `"${inspection.submitted_by_email || 'No email'}"`,
      `"${inspection.regional_sales_leader_name || 'Unassigned'}"`,
      `"${inspection.area_sales_manager_name || 'Direct to RVP'}"`,
      `"${getUserTotalPoints(getSubmittedByName(inspection))}"`,
      `"${confidenceScore}"`,
      `"${inspection.human_review_status === 'confirmed' ? 'APPROVED' : (inspection.human_review_status === 'pending' ? 'PENDING' : 'NOT REVIEWED')}"`,
      `"${inspection.notes || parsed.maintenance_notes || 'N/A'}"`,
      // `"${inspection.main_page_voice_audio && inspection.main_page_voice_audio.length > 0 ? `${inspection.main_page_voice_audio.length} audio file(s)` : 'No voice audio'}"`, // BLOCKED OUT - Voice Audio column
      // `"${inspection.main_page_voice_notes || 'No voice notes'}"`, // BLOCKED OUT - Voice Notes column
      `"${inspection.main_page_attachments && inspection.main_page_attachments.length > 0 ? `${inspection.main_page_attachments.length} attachment(s)` : 'No attachments'}"`,
      `"${inspection.additional_images && inspection.additional_images.length > 0 ? `${inspection.additional_images.length} image(s)` : 'No additional images'}"`,
      `"${extractAdditionalNotes(inspection) || 'No additional notes'}"`,
      `"${metadataString}"`
    ];
    csvRows.push(row.join(','));
  });

  return csvRows.join('\n');
};

/**
 * Download CSV content as a file
 * @param {string} csvContent - CSV content string
 * @param {string} filename - Filename for download
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};


